import { useState, useCallback, useRef, useEffect } from 'react';
import {
  WorkerMessage,
  WorkerMessageType,
  WorkerProgressData,
  ColoredSTL,
  PartialColorResult,
  MaterialProperties,
  VirtualFile,
  SceneLight,
  SceneOverrides,
  DetectColorsResponseData,
  GenerateSTLResponseData,
  type PiPCamera,
  type CameraPathWaypoint,
} from '@/worker/types';
import type { InteractiveConfig } from '@shared/types';
import type { AnnotationDef, MotionDef, CloneDef } from '@/worker/types';
import OpenSCADError from '@/lib/OpenSCADError';
import { WorkerPool, POOL_SIZE } from '@/worker/workerPool';
import { usePerformanceStore } from '@/stores/performanceStore';
import {
  prepareSvgFragments,
  makeWorkerFileSvgResolver,
  type SvgFragmentFile,
} from '@/utils/svgFragmentPrepare';

export type ColoredOutput = {
  color: [number, number, number, number];
  blob: Blob;
  isGhost?: boolean; // True for % modifier - preview only, exclude from export
  excludedFromExport?: boolean; // True for exclude_from_export() - show in preview (toggleable), exclude from export
  material?: MaterialProperties; // Per-object PBR material properties
  /** Texture scripts/images linked to this geometry via texture_script()/texture_svg()/texture_png() in SCAD code */
  textureScripts?: Array<{
    filename: string;
    channel: string;
    /** "script" = JS canvas worker, "image" = static image (SVG/PNG/JPG/WebP) rasterization */
    type: 'script' | 'image';
    /** Explicit rasterization width (image only; undefined = infer from source) */
    width?: number;
    /** Explicit rasterization height (image only; undefined = infer from source) */
    height?: number;
    /** Per-instance key/value pairs passed from OpenSCAD as the third argument to texture_script() */
    props?: Record<string, unknown>;
    /** Optional face constraint — restricts texture to faces pointing in this direction */
    face?: string;
  }>;
  /** Set when this geometry was wrapped with interactive() in SCAD source. */
  interactive?: InteractiveConfig;
  /** Set when this geometry was wrapped with annotation() in SCAD source. */
  annotation?: AnnotationDef;
  /** Set when this geometry was wrapped with motion() in SCAD source. */
  motion?: MotionDef;
  /** Set when this geometry was wrapped with clone_ref() in SCAD source. */
  cloneKey?: string;
  /** Set when this geometry was wrapped with shader_material("file.js") in SCAD source. */
  shaderScript?: string;
  /** Compile run this output belongs to — used to detect new compile in useGeometryLifecycle */
  compileId: string;
};

// Files to be synced to the worker
export interface WorkerFile {
  filename: string;
  content: string | ArrayBuffer;
  isBinary?: boolean;
}

/**
 * Debounce delay in milliseconds applied before dispatching a compile request
 * to the web worker. Prevents hammering the worker on every keystroke.
 * Cache hits inside the worker are still instant — the debounce only limits
 * how often we send messages across the thread boundary.
 */
const COMPILE_DEBOUNCE_MS = 400;

/** Convert a ColoredSTL (Uint8Array) from the worker into a ColoredOutput (Blob) */
function coloredSTLToOutput(
  coloredSTL: ColoredSTL,
  compileId: string,
): ColoredOutput {
  return {
    color: coloredSTL.color,
    blob: new Blob([coloredSTL.data], { type: 'model/stl' }),
    isGhost: coloredSTL.isGhost,
    excludedFromExport: coloredSTL.excludedFromExport,
    material: coloredSTL.material,
    textureScripts: coloredSTL.textureScripts,
    interactive: coloredSTL.interactive,
    annotation: coloredSTL.annotation,
    motion: coloredSTL.motion,
    cloneKey: coloredSTL.cloneKey,
    shaderScript: coloredSTL.shaderScript,
    compileId,
  };
}

export interface UseOpenSCADOptions {
  /**
   * Called once when the first compile result (geometry) is produced.
   * Intended for blog playgrounds to signal that the heavy init phase is
   * complete so the next queued playground can begin its own init.
   */
  onFirstResult?: () => void;
}

export function useOpenSCAD({ onFirstResult }: UseOpenSCADOptions = {}) {
  const [isCompiling, setIsCompiling] = useState(false);
  const [error, setError] = useState<OpenSCADError | Error | undefined>();
  const [isError, setIsError] = useState(false);
  const [output, setOutput] = useState<Blob | undefined>();
  const [coloredOutputs, setColoredOutputs] = useState<ColoredOutput[]>([]);
  const [lights, setLights] = useState<SceneLight[]>([]);
  const [pipCameras, setPipCameras] = useState<PiPCamera[]>([]);
  const [cameraPath, setCameraPath] = useState<CameraPathWaypoint[]>([]);
  const [sceneOverrides, setSceneOverrides] = useState<SceneOverrides>({});
  const [clones, setClones] = useState<CloneDef[]>([]);
  const [progress, setProgress] = useState<WorkerProgressData | null>(null);

  /**
   * Single worker kept alive for the app lifetime — used for EXPORT and FS
   * operations only.  It is also the fallback for single-color / no-ghost
   * models where the fast-path PREVIEW message is sent directly.
   */
  const workerRef = useRef<Worker | null>(null);

  /**
   * Worker pool — 4 pre-warmed workers used for parallel multi-color compiles.
   */
  const poolRef = useRef<WorkerPool | null>(null);

  /** Pending debounce timer handle */
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * The compileId of the most recently dispatched compile request.
   * Stored as a ref so the async callbacks can check for staleness without
   * closure staleness.
   */
  const currentCompileIdRef = useRef<string | null>(null);

  /**
   * Whether we have received the first PARTIAL_RESULT for the current compile.
   * We keep old geometry visible until the first partial arrives, then swap.
   */
  const firstPartialReceivedRef = useRef(false);

  /**
   * Whether onFirstResult has already been called for the lifetime of this
   * hook instance. Fired at most once, on the very first geometry produced.
   */
  const onFirstResultCalledRef = useRef(false);

  /** Stable ref to the onFirstResult callback so eventHandler doesn't need it as a dep. */
  const onFirstResultRef = useRef(onFirstResult);
  onFirstResultRef.current = onFirstResult;

  /**
   * Fire onFirstResult exactly once per hook lifetime, regardless of whether
   * the compile succeeded or failed. This ensures the stagger queue is always
   * unblocked so subsequent playgrounds can mount.
   */
  const fireOnFirstResult = useCallback(() => {
    if (!onFirstResultCalledRef.current) {
      onFirstResultCalledRef.current = true;
      onFirstResultRef.current?.();
    }
  }, []);

  /** Pending resolve for the FS_WRITE_BATCH acknowledgement on the single worker */
  const fsSyncResolveRef = useRef<(() => void) | null>(null);

  /** True while the single (main) worker is processing a PREVIEW request */
  const mainWorkerBusyRef = useRef(false);

  /** Read the setCompileWorkerStats action from the per-viewer performance store */
  const setCompileWorkerStats = usePerformanceStore(
    (state) => state.setCompileWorkerStats,
  );
  const pushWorkerSample = usePerformanceStore(
    (state) => state.pushWorkerSample,
  );
  const textureWorkers = usePerformanceStore((state) => state.textureWorkers);
  const textureWorkersRef = useRef(textureWorkers);
  textureWorkersRef.current = textureWorkers;

  /**
   * Recompute and push compile worker stats to the performance store.
   * Called whenever the main worker or pool busy state changes.
   * Stored as a ref so it can be called from eventHandler without being a dep.
   */
  const updateCompileWorkerStats = useCallback(
    (poolActive: number, poolTotal: number) => {
      const mainActive = mainWorkerBusyRef.current ? 1 : 0;
      const compileActive = poolActive + mainActive;
      const compileTotal = poolTotal + 1; // +1 for the main worker
      setCompileWorkerStats({ active: compileActive, total: compileTotal });
      // Record a combined worker sample for the history graph
      const tw = textureWorkersRef.current;
      pushWorkerSample({
        t: Date.now(),
        active: compileActive + tw.active,
        total: compileTotal + tw.total,
      });
    },
    [setCompileWorkerStats, pushWorkerSample],
  );
  const updateCompileWorkerStatsRef = useRef(updateCompileWorkerStats);
  updateCompileWorkerStatsRef.current = updateCompileWorkerStats;

  // ---------------------------------------------------------------------------
  // Single-worker event handler (used for EXPORT / fast-path PREVIEW)
  // ---------------------------------------------------------------------------
  const eventHandler = useCallback((event: MessageEvent) => {
    const { type, data } = event.data;

    if (type === WorkerMessageType.PROGRESS) {
      setProgress(data);
      return;
    }

    // Stream a single compiled color into the preview as soon as it is ready
    if (type === WorkerMessageType.PARTIAL_RESULT) {
      const partial = data as PartialColorResult;
      if (partial.compileId !== currentCompileIdRef.current) {
        // Stale result from a superseded compile — discard
        return;
      }
      const newOutput = coloredSTLToOutput(
        partial.coloredSTL,
        partial.compileId,
      );
      if (!firstPartialReceivedRef.current) {
        // First partial: replace old geometry entirely so we don't show a
        // mix of old and new colors
        firstPartialReceivedRef.current = true;
        setColoredOutputs([newOutput]);
        fireOnFirstResult();
      } else {
        setColoredOutputs((prev) => [...prev, newOutput]);
      }
      return;
    }

    // Handle FS_WRITE_BATCH acknowledgement to unblock syncFiles
    if (type === WorkerMessageType.FS_WRITE_BATCH) {
      fsSyncResolveRef.current?.();
      fsSyncResolveRef.current = null;
      return;
    }

    if (event.data.err) {
      setError(event.data.err);
      setIsError(true);
      setOutput(undefined);
      setColoredOutputs([]);
      setLights([]);
      setSceneOverrides({});
      setClones([]);
      setProgress(null);
      // Release the stagger slot even on error so the next playground can mount
      fireOnFirstResult();
    } else if (data && (data as any).output) {
      // Discard final responses from superseded compiles
      if (
        (data as any).compileId &&
        (data as any).compileId !== currentCompileIdRef.current
      ) {
        return;
      }

      const blob = new Blob([(data as any).output], {
        type: (data as any).fileType === 'stl' ? 'model/stl' : 'image/svg+xml',
      });
      setOutput(blob);

      // Handle lights if present in the response.
      // Note: the PREVIEW (single-worker) path does not include lights or
      // sceneOverrides in its response — those come from the detection pass
      // which already called setLights/setSceneOverrides before dispatching
      // the compile. Only update them when the response explicitly carries data.
      if ((data as any).lights && (data as any).lights.length > 0) {
        setLights((data as any).lights);
      }

      // Handle scene overrides only when the response explicitly carries them
      // (multi-color pool path). The fast PREVIEW path carries no overrides —
      // they were already applied from the detection pass and must not be cleared.
      if ((data as any).sceneOverrides) {
        setSceneOverrides((data as any).sceneOverrides);
      }

      // The coloredOutputs have already been populated incrementally via
      // PARTIAL_RESULT messages. Only fall back to setting them from the
      // final response when no partial results were received (e.g. cache-miss
      // on a single-color model that fell through to the regular preview path).
      if (!firstPartialReceivedRef.current) {
        if (
          (data as any).coloredOutputs &&
          (data as any).coloredOutputs.length > 0
        ) {
          const responseCompileId =
            (data as any).compileId ?? currentCompileIdRef.current ?? '';
          const coloredBlobs: ColoredOutput[] = (
            data as any
          ).coloredOutputs.map((coloredSTL: ColoredSTL) =>
            coloredSTLToOutput(coloredSTL, responseCompileId),
          );
          setColoredOutputs(coloredBlobs);
          fireOnFirstResult();
        } else {
          setColoredOutputs([]);
          // No geometry produced but compile finished — still release the slot
          fireOnFirstResult();
        }
      }
      setProgress(null);
    }
    // Main worker is now idle — update the worker stats display
    mainWorkerBusyRef.current = false;
    updateCompileWorkerStatsRef.current(
      poolRef.current ? poolRef.current.getActiveCount() : 0,
      POOL_SIZE,
    );
    setIsCompiling(false);
  }, []);

  // ---------------------------------------------------------------------------
  // Lifecycle: create worker + pool on mount, terminate on unmount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    workerRef.current = new Worker(
      new URL('../worker/worker.ts', import.meta.url),
      { type: 'module' },
    );
    workerRef.current.addEventListener('message', eventHandler);

    const pool = new WorkerPool();
    pool.onBusyStateChange = (active, total) => {
      updateCompileWorkerStats(active, total);
    };
    poolRef.current = pool;

    // Report the total count immediately so the display shows "0 / 5 workers"
    // as soon as the pool is created, even before any compile starts.
    updateCompileWorkerStats(0, POOL_SIZE);

    return () => {
      workerRef.current?.terminate();
      poolRef.current?.terminate();
      // Reset counts when this viewer is destroyed
      setCompileWorkerStats({ active: 0, total: 0 });
    };
  }, [eventHandler, updateCompileWorkerStats, setCompileWorkerStats]);

  // ---------------------------------------------------------------------------
  // syncFiles — writes project files into the single worker's WASM FS
  // ---------------------------------------------------------------------------
  const syncFiles = useCallback(async (files: WorkerFile[]) => {
    if (!workerRef.current || files.length === 0) return;

    const virtualFiles: VirtualFile[] = files.map((f) => ({
      path: f.filename,
      content: f.content,
      isBinary: f.isBinary,
    }));

    const message: WorkerMessage = {
      type: WorkerMessageType.FS_WRITE_BATCH,
      data: { files: virtualFiles },
    };

    // Wait for the worker to acknowledge the write before continuing
    await new Promise<void>((resolve) => {
      fsSyncResolveRef.current = resolve;
      workerRef.current!.postMessage(message);
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Pool-based parallel multi-color compile
  // ---------------------------------------------------------------------------
  /**
   * Run a full multi-color compile using the worker pool.
   *
   * Steps:
   *  1. Sync project files to all pool workers.
   *  2. Run DETECT_COLORS on one pool worker.
   *  3. If single opaque color with no ghosts/materials → fall back to the
   *     single fast-path PREVIEW on the normal worker.
   *  4. Otherwise fan out GENERATE_COLOR_STL + GENERATE_GHOST_STL jobs
   *     across the pool concurrently, streaming partials as each resolves.
   *  5. Assemble the final response.
   */
  const compileWithPool = useCallback(
    async (
      code: string,
      params: WorkerMessage['data'] extends { params: infer P } ? P : never[],
      files: WorkerFile[],
      compileId: string,
    ) => {
      const pool = poolRef.current!;

      // Sync project files to all pool workers
      if (files.length > 0) {
        const virtualFiles: VirtualFile[] = files.map((f) => ({
          path: f.filename,
          content: f.content,
          isBinary: f.isBinary,
        }));
        await pool.syncFiles(virtualFiles);
      }

      // Guard: abort if a newer compile has been requested
      if (currentCompileIdRef.current !== compileId) return;

      // Pre-parse SVG partial-imports on the main thread (DOMParser is not
      // universally available in WorkerGlobalScope, so we can't do this in
      // the worker).  The synthesized fragment SVGs ride along with each
      // compile job; the rewritten code points at their virtual paths.
      let preparedCode = code;
      let svgFragmentFiles: SvgFragmentFile[] = [];
      try {
        const prep = await prepareSvgFragments(
          code,
          makeWorkerFileSvgResolver(files),
        );
        preparedCode = prep.rewrittenCode;
        svgFragmentFiles = prep.fragmentFiles;
      } catch (err) {
        if (currentCompileIdRef.current !== compileId) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setIsError(true);
        setIsCompiling(false);
        setProgress(null);
        fireOnFirstResult();
        return;
      }

      if (currentCompileIdRef.current !== compileId) return;

      setProgress({ progress: 10, message: 'Detecting colors...' });

      // Step 1: Detect colors.  Run detection on the rewritten source so
      // SVG fragment imports don't trip up the detect pass either.
      let detection: DetectColorsResponseData;
      try {
        detection = await pool.detectColors({
          code: preparedCode,
          params: params as any,
        });
      } catch (err) {
        if (currentCompileIdRef.current !== compileId) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setIsError(true);
        setIsCompiling(false);
        setProgress(null);
        // Release the stagger slot even on error so the next playground can mount
        fireOnFirstResult();
        return;
      }

      if (currentCompileIdRef.current !== compileId) return;

      const {
        colors: detectedItems,
        lights: detectedLights,
        pipCameras: detectedPiPCameras,
        cameraPath: detectedCameraPath,
        sceneOverrides: detectedSceneOverrides,
        clones: detectedClones,
      } = detection;

      // Apply scene_* overrides, lights, PiP cameras, camera path, and clones immediately after
      // detection so they are available regardless of which compile path is taken.
      setSceneOverrides(detectedSceneOverrides ?? {});
      if (detectedLights.length > 0) {
        setLights(detectedLights);
      } else {
        setLights([]);
      }
      setPipCameras(detectedPiPCameras ?? []);
      setCameraPath(detectedCameraPath ?? []);
      setClones(detectedClones ?? []);

      const regularColors = detectedItems.filter((item) => !item.isGhost);
      const ghostItems = detectedItems.filter((item) => item.isGhost);

      const hasTransparency = regularColors.some((item) => item.color[3] < 1);
      const hasMultipleColors = regularColors.length > 1;
      const hasGhosts = ghostItems.length > 0;
      const hasMaterials = regularColors.some((item) => item.material != null);
      const hasExcluded = regularColors.some((item) => item.excludedFromExport);
      const hasTextureScripts = regularColors.some(
        (item) => item.textureScripts != null && item.textureScripts.length > 0,
      );
      const hasInteractive = regularColors.some(
        (item) => item.interactive != null,
      );
      const hasAnnotations = regularColors.some(
        (item) => item.annotation != null,
      );
      const hasMotion = regularColors.some((item) => item.motion != null);
      const hasCloneKeys = regularColors.some((item) => item.cloneKey != null);
      const hasClones = (detectedClones ?? []).length > 0;

      console.log('[useOpenSCAD] Detection result:', {
        regularColors: regularColors.length,
        hasTransparency,
        hasMultipleColors,
        hasGhosts,
        hasMaterials,
        hasExcluded,
        hasTextureScripts,
        hasInteractive,
        hasAnnotations,
        textureScripts: regularColors
          .filter((c) => c.textureScripts?.length)
          .flatMap((c) => c.textureScripts!),
      });

      // Fast path: single opaque color, no ghosts, materials, texture scripts, interactive, annotation, or motion regions
      // → send the cheap PREVIEW message to the single worker instead
      if (
        !hasTransparency &&
        !hasMultipleColors &&
        !hasGhosts &&
        !hasMaterials &&
        !hasExcluded &&
        !hasTextureScripts &&
        !hasInteractive &&
        !hasAnnotations &&
        !hasMotion &&
        !hasCloneKeys &&
        !hasClones
      ) {
        console.log(
          '[useOpenSCAD] Pool: falling back to single-worker PREVIEW (single opaque color)',
        );
        // Sync files to the single worker (may already be in sync, but cheap)
        if (files.length > 0) {
          await syncFiles(files);
        }
        if (currentCompileIdRef.current !== compileId) return;
        const message: WorkerMessage = {
          type: WorkerMessageType.PREVIEW,
          data: {
            code: preparedCode,
            params: params as any,
            fileType: 'stl',
            compileId,
            svgFragmentFiles,
          },
        };
        mainWorkerBusyRef.current = true;
        updateCompileWorkerStats(
          poolRef.current ? poolRef.current.getActiveCount() : 0,
          POOL_SIZE,
        );
        workerRef.current?.postMessage(message);
        return;
      }

      // Multi-color / ghost path: fan out jobs in parallel
      const totalItems = regularColors.length + (hasGhosts ? 1 : 0);
      let completedItems = 0;

      // Helper: call when a partial result arrives
      const emitPartial = (stl: ColoredSTL) => {
        if (currentCompileIdRef.current !== compileId) return;
        const newOutput = coloredSTLToOutput(stl, compileId);
        if (!firstPartialReceivedRef.current) {
          firstPartialReceivedRef.current = true;
          setColoredOutputs([newOutput]);
          fireOnFirstResult();
        } else {
          setColoredOutputs((prev) => [...prev, newOutput]);
        }
        completedItems++;
        const pct = 20 + Math.round((completedItems / totalItems) * 70);
        setProgress({
          progress: pct,
          message: `Part ${completedItems}/${totalItems}...`,
        });
      };

      setProgress({
        progress: 20,
        message: `Generating ${totalItems} part(s)...`,
      });

      // Launch all color jobs concurrently
      const colorPromises = regularColors.map((item) =>
        pool
          .generateColorSTL({
            code: preparedCode,
            params: params as any,
            targetColor: item.color,
            isGhost: false,
            targetMaterial: item.material,
            targetExcludedFromExport: item.excludedFromExport,
            svgFragmentFiles,
          })
          .then((res: GenerateSTLResponseData) => {
            if (currentCompileIdRef.current !== compileId) return null;
            if (res.stlData && res.stlData.length > 84) {
              const triangleCount = new DataView(
                res.stlData.buffer,
                res.stlData.byteOffset + 80,
                4,
              ).getUint32(0, true);
              if (triangleCount > 0) {
                const stl: ColoredSTL = {
                  color: item.color,
                  data: res.stlData,
                  isGhost: false,
                  excludedFromExport: item.excludedFromExport,
                  material: item.material,
                  textureScripts: item.textureScripts,
                  interactive: item.interactive,
                  annotation: item.annotation,
                  motion: item.motion,
                  cloneKey: item.cloneKey,
                };
                emitPartial(stl);
                return stl;
              }
            }
            return null;
          }),
      );

      // Launch ghost job concurrently with color jobs
      const ghostPromise = hasGhosts
        ? pool
            .generateGhostSTL({
              code: preparedCode,
              params: params as any,
              svgFragmentFiles,
            })
            .then((res: GenerateSTLResponseData) => {
              if (currentCompileIdRef.current !== compileId) return null;
              if (res.stlData && res.stlData.length > 84) {
                const triangleCount = new DataView(
                  res.stlData.buffer,
                  res.stlData.byteOffset + 80,
                  4,
                ).getUint32(0, true);
                if (triangleCount > 0) {
                  const stl: ColoredSTL = {
                    color: ghostItems[0].color,
                    data: res.stlData,
                    isGhost: true,
                  };
                  emitPartial(stl);
                  return stl;
                }
              }
              return null;
            })
        : Promise.resolve(null);

      // Wait for everything
      const [resolvedColors, resolvedGhost] = await Promise.all([
        Promise.all(colorPromises),
        ghostPromise,
      ]);

      if (currentCompileIdRef.current !== compileId) return;

      const allSTLs: ColoredSTL[] = [
        ...resolvedColors.filter((s): s is ColoredSTL => s !== null),
        ...(resolvedGhost ? [resolvedGhost] : []),
      ];

      if (allSTLs.length === 0) {
        // Nothing was rendered — fall back to a plain PREVIEW on the single worker
        console.log(
          '[useOpenSCAD] Pool: no STLs generated, falling back to single-worker PREVIEW',
        );
        if (files.length > 0) {
          await syncFiles(files);
        }
        if (currentCompileIdRef.current !== compileId) return;
        const message: WorkerMessage = {
          type: WorkerMessageType.PREVIEW,
          data: {
            code: preparedCode,
            params: params as any,
            fileType: 'stl',
            compileId,
            svgFragmentFiles,
          },
        };
        workerRef.current?.postMessage(message);
        return;
      }

      // Synthesise a final output blob from the first non-ghost STL (or first overall)
      const mainSTL = allSTLs.find((s) => !s.isGhost) ?? allSTLs[0];
      const outputBlob = new Blob([mainSTL.data], { type: 'model/stl' });

      setOutput(outputBlob);
      setProgress(null);
      setIsCompiling(false);

      console.log(
        `[useOpenSCAD] Pool compile done — ${allSTLs.length} STL(s), ${detectedLights.length} light(s)`,
      );
    },
    [syncFiles],
  );

  // ---------------------------------------------------------------------------
  // compileScad — debounced entry point
  // ---------------------------------------------------------------------------
  const compileScad = useCallback(
    (code: string, files: WorkerFile[] = []) => {
      // Cancel any pending debounced compile
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(async () => {
        debounceTimerRef.current = null;

        setIsCompiling(true);
        setError(undefined);
        setIsError(false);
        setProgress({ progress: 0, message: 'Starting...' });

        // Generate a unique id for this compile run.
        // Results with a different id will be discarded.
        const newCompileId =
          Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
        currentCompileIdRef.current = newCompileId;
        firstPartialReceivedRef.current = false;

        // Reuse existing worker if available, only create new one if needed
        if (!workerRef.current) {
          workerRef.current = new Worker(
            new URL('../worker/worker.ts', import.meta.url),
            { type: 'module' },
          );
          workerRef.current.addEventListener('message', eventHandler);
        }

        if (!poolRef.current) {
          poolRef.current = new WorkerPool();
        }

        await compileWithPool(code, [] as any, files, newCompileId);
      }, COMPILE_DEBOUNCE_MS);
    },
    [eventHandler, compileWithPool],
  );

  return {
    compileScad,
    syncFiles,
    isCompiling,
    output,
    coloredOutputs,
    lights,
    pipCameras,
    cameraPath,
    sceneOverrides,
    clones,
    error,
    isError,
    progress,
  };
}
